import { ReactNode, FunctionComponent, useEffect } from 'react';
import { Text, Group, Menu } from '@mantine/core';
import { useNavigate } from 'react-router-dom';

import ApiIcon from '@assets/api-icon.svg?react';
import ArrowIcon from '@assets/icon-chevron-selector-vertical.svg?react';
import ChatSupportIcon from '@assets/chat-support-icon.svg?react';
import DocumentationIcon from '@assets/documentation-icon.svg?react';
import FlowsIcon from '@assets/icon-data-flow.svg?react';
import FullLogo from '@assets/logo-full.svg?react';
import LinkIcon from '@assets/icon-link-external.svg?react';
import ModelIcon from '@assets/icon-container.svg?react';
import OrganizationIcon from '@assets/organization-icon.svg?react';
import SettingsIcon from '@assets/settings-icon.svg?react';
import { ProfileMenu } from '@components/TopBar/ProfileMenu';
import { createCredentials } from '@contexts/AuthProvider';
import { useAuthContext, useOrganizationQuery, useUnmount } from '@hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_REDIRECT_ROUTE,
  LOCAL_STORAGE_AUTH_TOKEN_PREFIX,
  LOCAL_STORAGE_LAST_USED_CLIENT_ID_KEY,
} from '@config/app';

type SidebarItemProps = {
  Icon: FunctionComponent;
  display: string;
  onClick: () => void;
  RightIcon?: FunctionComponent;
};

function SidebarItem({ Icon, display, onClick, RightIcon }: SidebarItemProps) {
  return (
    <Group
      className="tw-cursor-pointer tw-rounded-lg tw-p-3 tw-text-gray-600 hover:tw-bg-gray-50 hover:tw-text-primary-600"
      onClick={onClick}
    >
      <Icon />
      <Text className="!tw-text-md !tw-font-medium">{display}</Text>
      <div className="tw-flex tw-grow tw-justify-end">{RightIcon ? <RightIcon /> : null}</div>
    </Group>
  );
}

function OrganizationMenu() {
  const { client, jwt, scopes, setCredentials } = useAuthContext();
  const currentClientId = (jwt as any)?.client_id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: currentOrganization } = useOrganizationQuery();
  const { data: organizationsData } = useQuery<any>({
    queryKey: ['organizations'],
    queryFn: () => client!.makeGetRequest('/organizations'),
  });

  const onSelectOrganization = (clientId: string) => {
    window.localStorage.setItem(LOCAL_STORAGE_LAST_USED_CLIENT_ID_KEY, clientId);
    const credentials = createCredentials(clientId);
    const hasStoredJWT = window.localStorage.getItem(`${LOCAL_STORAGE_AUTH_TOKEN_PREFIX}/${clientId}`);
    // Previous JWT with possibly valid auth token and/or refresh token exist for this clientId
    if (hasStoredJWT) {
      setCredentials(credentials);
      queryClient.clear();
      // Might be on a organization specific resource like /models/las:model:abc, redirect to safe place
      navigate(DEFAULT_REDIRECT_ROUTE);
    } else {
      credentials.initiateOAuthFlow();
    }
  };

  return (
    <Menu>
      <Menu.Target>
        <div className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border-1.5 tw-border-gray-200 tw-p-2 tw-shadow-sm hover:tw-bg-gray-50">
          <Group>
            <div className="tw-rounded-lg tw-border-2 tw-border-gray-300 tw-p-1">
              <OrganizationIcon />
            </div>
            <Text className="!tw-text-md !tw-font-medium !tw-text-gray-900">{currentOrganization?.name}</Text>
          </Group>
          <ArrowIcon className="tw-text-gray-400" />
        </div>
      </Menu.Target>

      <Menu.Dropdown>
        {(organizationsData?.organizations ?? []).map((organization: any) => {
          return (
            <Menu.Item
              onClick={() => onSelectOrganization(organization.clientId)}
              rightSection={
                organization.clientId === currentClientId ? (
                  <div className="tw-rounded-xl tw-border-1.5 tw-border-success-200 tw-bg-success-25 tw-px-1">
                    <Text className="!tw-text-xs !tw-text-success-500">Current</Text>
                  </div>
                ) : null
              }
            >
              <div className="tw-p-0.5">
                <Text className="!tw-text-sm !tw-font-medium !tw-text-gray-600 hover:!tw-text-gray-900">
                  {organization?.name}
                </Text>
              </div>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

type SidebarProps = {
  children: ReactNode;
};

export function Sidebar({ children }: SidebarProps) {
  const navigate = useNavigate();

  useEffect(() => {
    window._chatlio?.hide();
  }, []);

  useUnmount(() => {
    window._chatlio?.show();
  });

  return (
    <div className="tw-flex tw-flex-row">
      <div className="tw-sticky tw-top-0 tw-flex tw-h-[100vh] tw-w-[280px] tw-border-r-1.5 tw-border-gray-200 tw-bg-white">
        <div className="tw-flex tw-grow tw-flex-col tw-justify-between">
          <div>
            <div className="tw-flex tw-flex-row tw-items-center tw-border-b-1.5 tw-border-gray-200 tw-px-9 tw-py-5">
              <FullLogo className="tw-h-[40px] tw-cursor-pointer" onClick={() => navigate('/')} />
            </div>
            <div className="tw-mt-5 tw-p-2">
              <div className="tw-p-3">
                <OrganizationMenu />
              </div>
              <div className="tw-px-3">
                <SidebarItem Icon={FlowsIcon} display="Flows" onClick={() => navigate('/flows')} />
                <SidebarItem Icon={ModelIcon} display="Models" onClick={() => navigate('/models')} />
              </div>
            </div>
          </div>
          <div>
            <div className="tw-p-5">
              <SidebarItem Icon={ApiIcon} display="API" onClick={() => navigate('/appclients')} />
              <SidebarItem Icon={SettingsIcon} display="Settings" onClick={() => navigate('/settings/profile')} />
              <SidebarItem
                Icon={DocumentationIcon}
                display="Documentation"
                onClick={() => window.open('https://docs.cradl.ai', '_blank')}
                RightIcon={() => <LinkIcon className="tw-h-[22px] tw-w-[22px] tw-stroke-1" />}
              />
              <SidebarItem
                Icon={ChatSupportIcon}
                display="Chat Support"
                onClick={() => window._chatlio?.show({ expanded: true })}
              />
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-border-t-1.5 tw-border-gray-200 tw-p-5">
              <ProfileMenu />
            </div>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
